import { supabase } from '@/integrations/supabase/client';

export type NotificationType = 
  | 'bill_reminder' 
  | 'payment_confirmation' 
  | 'consumption_alert' 
  | 'system_maintenance' 
  | 'general_info';

// Using the existing notifications table structure from Supabase
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  target_region: string | null;
  is_active: boolean;
  created_at: string;
  created_by_admin: boolean;
  // Add client-side properties for local state management
  read?: boolean;
  user_id?: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_enabled: boolean;
  push_enabled: boolean;
  sms_enabled: boolean;
  bill_reminders: boolean;
  payment_confirmations: boolean;
  consumption_alerts: boolean;
  system_maintenance: boolean;
  general_info: boolean;
  created_at: string;
  updated_at: string;
}

export class NotificationService {
  // Local storage keys for client-side notification management
  private static STORAGE_KEYS = {
    READ_NOTIFICATIONS: 'read_notifications',
    PREFERENCES: 'notification_preferences',
  };

  static async createNotification(
    title: string,
    message: string,
    type: NotificationType,
    targetRegion?: string,
    organizationId?: string
  ): Promise<Notification | null> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          title,
          message,
          type,
          target_region: targetRegion,
          organization_id: organizationId ?? null,
          is_active: true,
          created_by_admin: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Fire-and-forget email broadcast to org users
      if (data?.id) {
        supabase.functions.invoke('broadcast-notification-email', {
          body: { notificationId: data.id },
        }).catch((e) => console.error('[Notifications] Broadcast email failed:', e));
      }

      return data as Notification;
    } catch (error) {
      console.error('Error creating notification:', error);
      return null;
    }
  }

  static async getUserNotifications(
    limit = 20,
    offset = 0
  ): Promise<Notification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      
      // Add read status from local storage
      const readNotifications = this.getReadNotifications();
      return (data || []).map(notification => ({
        ...notification,
        read: readNotifications.includes(notification.id),
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  static markAsRead(notificationId: string): boolean {
    try {
      const readNotifications = this.getReadNotifications();
      if (!readNotifications.includes(notificationId)) {
        readNotifications.push(notificationId);
        localStorage.setItem(
          this.STORAGE_KEYS.READ_NOTIFICATIONS,
          JSON.stringify(readNotifications)
        );
      }
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  static markAllAsRead(notifications: Notification[]): boolean {
    try {
      const allIds = notifications.map(n => n.id);
      localStorage.setItem(
        this.STORAGE_KEYS.READ_NOTIFICATIONS,
        JSON.stringify(allIds)
      );
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  static getUnreadCount(notifications: Notification[]): number {
    return notifications.filter(n => !n.read).length;
  }

  static getUserPreferences(): NotificationPreferences | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEYS.PREFERENCES);
      if (stored) {
        return JSON.parse(stored);
      }
      // Return default preferences
      return {
        id: 'default',
        user_id: 'current',
        email_enabled: true,
        push_enabled: true,
        sms_enabled: false,
        bill_reminders: true,
        payment_confirmations: true,
        consumption_alerts: true,
        system_maintenance: true,
        general_info: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      return null;
    }
  }

  static updatePreferences(
    preferences: Partial<Omit<NotificationPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ): boolean {
    try {
      const current = this.getUserPreferences();
      if (current) {
        const updated = {
          ...current,
          ...preferences,
          updated_at: new Date().toISOString(),
        };
        localStorage.setItem(
          this.STORAGE_KEYS.PREFERENCES,
          JSON.stringify(updated)
        );
      }
      return true;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return false;
    }
  }

  static subscribeToRealtime(
    onNotification: (notification: Notification) => void
  ) {
    return supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const notification = payload.new as Notification;
          onNotification({ ...notification, read: false });
        }
      )
      .subscribe();
  }

  private static getReadNotifications(): string[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEYS.READ_NOTIFICATIONS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }
}